import { TaxApiError } from "./errors";

/** Above this wait, the screen shows its error state instead of auto-retrying a 429. */
const RATE_LIMIT_AUTO_RETRY_MAX_SECONDS = 30;

function isRateLimited(error: TaxApiError): boolean {
  return error.status === 429 || error.code === "RATE_LIMITED";
}

/**
 * Query retry policy for the console QueryClient. Non-retryable TaxApiErrors
 * are never retried. A 429 retries at most once, and never when Retry-After
 * asks for more than 30 s. Other retryable failures keep two retries.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof TaxApiError && !error.retryable) return false;
  if (error instanceof TaxApiError && isRateLimited(error)) {
    if (
      error.retryAfterSeconds !== undefined &&
      error.retryAfterSeconds > RATE_LIMIT_AUTO_RETRY_MAX_SECONDS
    ) {
      return false;
    }
    return failureCount < 1;
  }
  return failureCount < 2;
}

/**
 * Delay before the next query attempt. When the API sent Retry-After, honour
 * it (plus 0–500 ms jitter so parallel queries do not stampede). Otherwise use
 * TanStack's default exponential backoff.
 */
export function queryRetryDelay(
  attemptIndex: number,
  error: unknown,
  random: () => number = Math.random,
): number {
  if (error instanceof TaxApiError && error.retryAfterSeconds !== undefined) {
    // Capped like the default backoff: a 503 that asks for minutes is not worth a parked query.
    return Math.min(error.retryAfterSeconds * 1000, 30_000) + Math.floor(random() * 501);
  }
  return Math.min(1000 * 2 ** attemptIndex, 30_000);
}
